import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Phone, MapPin, Clock, Mail, Send, CheckCircle2, Loader2 } from "lucide-react";
import { CONTACT, SERVICES } from "../data";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { VoiceButton } from "../components/VoiceButton";

const EMPTY = { name: "", email: "", phone: "", service: "", preferred_date: "", preferred_time: "", message: "" };

export default function Contact() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ ...EMPTY, service: params.get("service") || "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [slots, setSlots] = useState([]);
  const [dayOpen, setDayOpen] = useState(true);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Static availability (mirrors the old server rules): Mon–Fri 9am–5pm, Sat 10am–2pm, Sun closed.
  const onDateChange = (e) => {
    const date = e.target.value;
    setForm((f) => ({ ...f, preferred_date: date, preferred_time: "" }));
    if (!date) {
      setSlots([]);
      setDayOpen(true);
      return;
    }
    const HOURS_BY_DAY = { 1: [9, 17], 2: [9, 17], 3: [9, 17], 4: [9, 17], 5: [9, 17], 6: [10, 14] }; // JS getDay(): 0 = Sunday
    const slotLabel = (h) => {
      const suffix = h < 12 ? "AM" : "PM";
      let hh = h % 12;
      if (hh === 0) hh = 12;
      return `${hh}:00 ${suffix}`;
    };
    const d = new Date(date + "T12:00:00");
    const hours = HOURS_BY_DAY[d.getDay()];
    if (!hours) {
      setDayOpen(false);
      setSlots([]);
      return;
    }
    setDayOpen(true);
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const isToday = date === todayStr;
    const slots = [];
    for (let h = hours[0]; h < hours[1]; h++) {
      slots.push({ time: slotLabel(h), available: !(isToday && h <= now.getHours()) });
    }
    setSlots(slots);
  };

  const appendSpoken = (text) =>
    setForm((f) => ({ ...f, message: f.message ? `${f.message} ${text}` : text }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in your name, email, phone, and message.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("https://formsubmit.co/ajax/Lawrence@thetechsavvyco.com", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          _subject: `New booking request from ${form.name} — The Tech Savvy Company`,
          _template: "table",
          name: form.name,
          email: form.email,
          phone: form.phone,
          service: form.service || "Not specified",
          preferred_date: form.preferred_date || "Not specified",
          preferred_time: form.preferred_time || "Not specified",
          message: form.message,
        }),
      });
      if (!res.ok) throw new Error("send failed");
      setSent(true);
      setForm(EMPTY);
      toast.success("Thank you! We'll be in touch very soon.");
    } catch (err) {
      toast.error("Something went wrong. Please call us at " + CONTACT.phone);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact & Booking"
        title="Let's get you the help you need"
        subtitle="Call us for a friendly chat, or fill out the form below to book an appointment. We usually reply the same day."
      />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-[1fr,1.3fr] gap-14">
        {/* Contact info */}
        <Reveal>
          <div className="space-y-6">
            <a
              href={CONTACT.phoneHref}
              data-testid="contact-phone"
              className="flex items-center gap-5 rounded-2xl bg-primary text-white p-7 hover:bg-slate-800 transition-colors"
            >
              <span className="grid place-items-center h-14 w-14 rounded-2xl bg-accent shrink-0">
                <Phone className="h-7 w-7" />
              </span>
              <div>
                <p className="text-slate-300 text-sm">Call us now</p>
                <p className="font-heading font-bold text-2xl">{CONTACT.phone}</p>
              </div>
            </a>

            {[
              { icon: MapPin, label: "Service area", value: CONTACT.location },
              { icon: Clock, label: "Hours", value: CONTACT.hours },
              { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
            ].map((item) => {
              const Wrap = item.href ? "a" : "div";
              return (
                <Wrap
                  key={item.label}
                  {...(item.href ? { href: item.href } : {})}
                  className="flex items-center gap-5 rounded-2xl border border-border bg-card p-7"
                >
                  <span className="grid place-items-center h-14 w-14 rounded-2xl bg-accent/10 text-accent shrink-0">
                    <item.icon className="h-7 w-7" />
                  </span>
                  <div>
                    <p className="text-muted-foreground text-sm">{item.label}</p>
                    <p className="font-heading font-bold text-xl text-foreground">{item.value}</p>
                  </div>
                </Wrap>
              );
            })}

            <div className="rounded-2xl bg-secondary border border-border p-7">
              <p className="text-muted-foreground leading-relaxed">
                Prefer to talk to a real person? We'd love that. Give us a call and
                we'll happily answer any questions — no pressure, no jargon.
              </p>
            </div>
          </div>
        </Reveal>

        {/* Form */}
        <Reveal delay={0.12}>
          <div className="rounded-[2rem] border border-border bg-card p-8 lg:p-10 shadow-sm">
            {sent ? (
              <div data-testid="contact-success" className="text-center py-12">
                <CheckCircle2 className="h-16 w-16 text-accent mx-auto mb-6" />
                <h3 className="font-heading font-extrabold text-2xl text-foreground">Message sent!</h3>
                <p className="mt-3 text-muted-foreground text-lg leading-relaxed max-w-md mx-auto">
                  Thank you for reaching out. One of our friendly helpers will be in
                  touch very soon. Need help sooner? Call {CONTACT.phone}.
                </p>
                <button
                  data-testid="contact-send-another"
                  onClick={() => setSent(false)}
                  className="mt-8 inline-flex items-center justify-center h-14 px-8 rounded-full bg-accent text-white font-bold text-lg hover:bg-[hsl(196,82%,27%)] transition-colors"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                <h2 className="font-heading font-extrabold text-2xl text-foreground mb-2">
                  Book an appointment
                </h2>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Your name" required>
                    <input
                      data-testid="contact-name"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Jane Smith"
                      className="input-field"
                    />
                  </Field>
                  <Field label="Phone number" required>
                    <input
                      data-testid="contact-phone-input"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="(380) 000-0000"
                      className="input-field"
                    />
                  </Field>
                </div>

                <Field label="Email address" required>
                  <input
                    data-testid="contact-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@example.com"
                    className="input-field"
                  />
                </Field>

                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="What do you need help with?">
                    <select
                      data-testid="contact-service"
                      value={form.service}
                      onChange={update("service")}
                      className="input-field"
                    >
                      <option value="">Select a service</option>
                      {SERVICES.map((s) => (
                        <option key={s.slug} value={s.title}>{s.title}</option>
                      ))}
                      <option value="Something else">Something else</option>
                    </select>
                  </Field>
                  <Field label="Preferred date (optional)">
                    <input
                      data-testid="contact-date"
                      type="date"
                      min={new Date().toISOString().split("T")[0]}
                      value={form.preferred_date}
                      onChange={onDateChange}
                      className="input-field"
                    />
                  </Field>
                </div>

                {form.preferred_date && (
                  <div data-testid="availability-picker">
                    <span className="block font-semibold text-foreground mb-2">
                      Pick a time that suits you
                    </span>
                    {!dayOpen ? (
                      <p data-testid="availability-closed" className="text-muted-foreground bg-secondary border border-border rounded-xl p-4">
                        We're closed on this day. We're open Monday–Saturday — please choose another date.
                      </p>
                    ) : slots.length === 0 ? (
                      <p className="text-muted-foreground bg-secondary border border-border rounded-xl p-4">
                        No times to show for this date. Please pick another day or call us.
                      </p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {slots.map((s) => {
                          const selected = form.preferred_time === s.time;
                          return (
                            <button
                              key={s.time}
                              type="button"
                              data-testid={`slot-${s.time.replace(/[:\s]/g, "-")}`}
                              disabled={!s.available}
                              onClick={() => setForm((f) => ({ ...f, preferred_time: s.time }))}
                              className={`h-12 rounded-xl font-semibold text-base transition-colors ${
                                selected
                                  ? "bg-accent text-white"
                                  : s.available
                                  ? "bg-card border border-border text-foreground hover:border-accent"
                                  : "bg-muted text-muted-foreground/50 line-through cursor-not-allowed"
                              }`}
                            >
                              {s.time}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {form.preferred_time && (
                      <p data-testid="slot-selected" className="mt-3 text-accent font-semibold">
                        Great — we'll pencil you in for {form.preferred_time}.
                      </p>
                    )}
                  </div>
                )}

                <Field label="Tell us a little more" required>
                  <textarea
                    data-testid="contact-message"
                    value={form.message}
                    onChange={update("message")}
                    rows={4}
                    placeholder="For example: I just got a new iPhone and want help moving everything over from my old one."
                    className="input-field resize-none"
                  />
                </Field>

                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 h-14 rounded-full bg-accent text-white font-bold text-lg hover:bg-[hsl(196,82%,27%)] transition-colors disabled:opacity-60"
                >
                  {loading ? (
                    <><Loader2 className="h-5 w-5 animate-spin" /> Sending…</>
                  ) : (
                    <><Send className="h-5 w-5" /> Send Message</>
                  )}
                </button>
                <p className="text-sm text-muted-foreground text-center">
                  We'll never share your details. We only use them to help you.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </section>

      <style>{`
        .input-field {
          width: 100%;
          height: 3.5rem;
          border-radius: 0.9rem;
          border: 1px solid hsl(var(--border));
          background: hsl(var(--card));
          padding: 0 1rem;
          font-size: 1.0625rem;
          color: hsl(var(--foreground));
          outline: none;
          transition: border-color .15s, box-shadow .15s;
        }
        textarea.input-field { height: auto; padding: 0.85rem 1rem; }
        .input-field:focus {
          border-color: hsl(var(--accent));
          box-shadow: 0 0 0 3px hsl(var(--accent) / 0.15);
        }
      `}</style>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block font-semibold text-foreground mb-2">
      {label} {required && <span className="text-accent">*</span>}
    </span>
    {children}
  </label>
);
